import { useState, Fragment } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronRight, ChevronDown, ArrowUpCircle, ArrowDownCircle, Pencil, Trash2, User, Layers } from 'lucide-react';

/**
 * Tabella Magazzino con raggruppamento: unità serializzate (con matricola) dello
 * stesso modello (descrizione+marca+modello) vengono mostrate in un'unica riga
 * espandibile "— N pz" che si apre elencando le singole matricole.
 */

const statoBadge = (articolo) => {
  if (articolo.stato === 'venduto') {
    return (
      <Badge className="rounded-none bg-slate-500/15 text-slate-600 dark:text-slate-300 border border-slate-500/30 text-[10px]" title={articolo.venduto_cliente_nome}>
        <User className="h-3 w-3 mr-1" />
        {articolo.venduto_cliente_nome ? articolo.venduto_cliente_nome.substring(0, 12) : 'Venduto'}
      </Badge>
    );
  }
  if (articolo.stato === 'reso') {
    return <Badge className="rounded-none bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 text-[10px]">Reso</Badge>;
  }
  if (articolo.stato === 'dismesso') {
    return <Badge className="rounded-none bg-zinc-500/15 text-zinc-500 border border-zinc-500/30 text-[10px]">Dismesso</Badge>;
  }
  return <Badge className="rounded-none bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-[10px]">Disponibile</Badge>;
};

const groupKey = (a) =>
  `${(a.descrizione || '').trim().toLowerCase()}|${(a.marca || '').trim().toLowerCase()}|${(a.modello || '').trim().toLowerCase()}`;

/** Costruisce le righe: gruppi (>=2 articoli stesso modello) + singoli.
 * Raggruppa per descrizione+marca+modello indipendentemente da matricola:
 * così anche unità caricate solo con EAN diversi appaiono come "3 pz" totali. */
const buildRows = (articoli) => {
  const groups = new Map(); // key -> items
  const order = []; // mantiene ordine di prima apparizione
  for (const a of articoli) {
    const key = groupKey(a);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push({ type: 'group', key });
    }
    groups.get(key).push(a);
  }
  // Gruppi con 1 solo articolo → riga singola
  return order.map((entry) => {
    const items = groups.get(entry.key);
    if (items.length === 1) return { type: 'single', articolo: items[0] };
    return { type: 'group', key: entry.key, items };
  });
};

export default function ArticoliGroupedTable({ articoli, isSuperAdmin, onCarico, onScarico, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState({});
  const rows = buildRows(articoli);
  const colCount = isSuperAdmin ? 9 : 8;

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderAzioni = (articolo) => (
    <div className="flex gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-success" onClick={() => onCarico(articolo)} title="Carico" data-testid={`carico-${articolo.id}`}>
        <ArrowUpCircle className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-urgent" onClick={() => onScarico(articolo)} title="Scarico" data-testid={`scarico-${articolo.id}`}>
        <ArrowDownCircle className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => onEdit(articolo)} data-testid={`edit-articolo-${articolo.id}`}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => onDelete(articolo.id)} data-testid={`delete-articolo-${articolo.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const renderSingleRow = (articolo, isSubRow = false) => (
    <tr key={articolo.id} className={isSubRow ? 'bg-muted/30' : ''} data-testid={isSubRow ? `unit-row-${articolo.id}` : `articolo-row-${articolo.id}`}>
      <td className={`font-mono ${isSubRow ? 'pl-8 text-xs' : ''}`}>{articolo.codice}</td>
      <td>
        {isSubRow ? (
          <span className="text-xs text-muted-foreground">↳ unità singola</span>
        ) : (
          <>
            <div className="font-medium">{articolo.descrizione}</div>
            {(articolo.marca || articolo.modello) && (
              <div className="text-[10px] text-muted-foreground">{[articolo.marca, articolo.modello].filter(Boolean).join(' · ')}</div>
            )}
          </>
        )}
      </td>
      <td className="hidden md:table-cell">{articolo.categoria || '-'}</td>
      <td className="hidden md:table-cell font-mono text-xs">{articolo.matricola || '-'}</td>
      <td>{statoBadge(articolo)}</td>
      <td>
        <span className={articolo.quantita <= articolo.scorta_minima && articolo.stato === 'disponibile' ? 'text-warning font-bold' : ''}>
          {articolo.quantita} {articolo.unita_misura}
        </span>
      </td>
      {isSuperAdmin && <td className="hidden lg:table-cell font-mono">{articolo.prezzo_acquisto?.toFixed(2)} €</td>}
      <td className="hidden lg:table-cell font-mono">{articolo.prezzo_vendita?.toFixed(2)} €</td>
      <td>{renderAzioni(articolo)}</td>
    </tr>
  );

  const renderGroupRow = (group) => {
    const isOpen = !!expanded[group.key];
    const first = group.items[0];
    const disponibili = group.items.filter((i) => i.stato === 'disponibile');
    const vendute = group.items.filter((i) => i.stato === 'venduto');
    const rese = group.items.filter((i) => i.stato === 'reso' || i.stato === 'dismesso');
    const qtaDisponibile = disponibili.reduce((s, i) => s + (i.quantita || 0), 0);

    return (
      <Fragment key={group.key}>
        <tr
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => toggle(group.key)}
          data-testid={`group-row-${group.key.replace(/[^a-z0-9]/gi, '-')}`}
        >
          <td className="font-mono">
            <span className="flex items-center gap-1">
              {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <Layers className="h-3.5 w-3.5 text-cyan-500 shrink-0" />
            </span>
          </td>
          <td>
            <div className="font-medium">{first.descrizione}</div>
            <div className="text-[10px] text-muted-foreground">
              {[first.marca, first.modello].filter(Boolean).join(' · ')}
              {' — '}
              <span className="font-semibold text-cyan-600 dark:text-cyan-400">{group.items.length} unità</span>
            </div>
          </td>
          <td className="hidden md:table-cell">{first.categoria || '-'}</td>
          <td className="hidden md:table-cell font-mono text-xs text-muted-foreground">
            {group.items.length} unità
          </td>
          <td>
            <div className="flex flex-wrap gap-1">
              {disponibili.length > 0 && (
                <Badge className="rounded-none bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-[10px]">
                  {disponibili.length} disp.
                </Badge>
              )}
              {vendute.length > 0 && (
                <Badge className="rounded-none bg-slate-500/15 text-slate-600 dark:text-slate-300 border border-slate-500/30 text-[10px]">
                  {vendute.length} vend.
                </Badge>
              )}
              {rese.length > 0 && (
                <Badge className="rounded-none bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 text-[10px]">
                  {rese.length} rese
                </Badge>
              )}
            </div>
          </td>
          <td className="font-semibold">{qtaDisponibile} {first.unita_misura}</td>
          {isSuperAdmin && <td className="hidden lg:table-cell font-mono">{first.prezzo_acquisto?.toFixed(2)} €</td>}
          <td className="hidden lg:table-cell font-mono">{first.prezzo_vendita?.toFixed(2)} €</td>
          <td className="text-xs text-muted-foreground">{isOpen ? 'Chiudi' : 'Espandi'}</td>
        </tr>
        {isOpen && group.items.map((unit) => (
          <tr key={unit.id} className="bg-muted/30" data-testid={`unit-row-${unit.id}`}>
            <td className="font-mono text-xs pl-8">{unit.codice}</td>
            <td className="text-xs text-muted-foreground">↳ {unit.matricola ? 'matricola' : 'codice EAN'}</td>
            <td className="hidden md:table-cell"></td>
            <td className="hidden md:table-cell font-mono text-xs font-semibold">{unit.matricola || unit.codice_barre || '-'}</td>
            <td>{statoBadge(unit)}</td>
            <td className="font-mono text-xs">{unit.quantita} {unit.unita_misura}</td>
            {isSuperAdmin && <td className="hidden lg:table-cell font-mono text-xs">{unit.prezzo_acquisto?.toFixed(2)} €</td>}
            <td className="hidden lg:table-cell font-mono text-xs">{unit.prezzo_vendita?.toFixed(2)} €</td>
            <td>{renderAzioni(unit)}</td>
          </tr>
        ))}
      </Fragment>
    );
  };

  return (
    <div className="border border-border overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Codice</th>
            <th>Descrizione</th>
            <th className="hidden md:table-cell">Categoria</th>
            <th className="hidden md:table-cell">Matricola</th>
            <th>Stato</th>
            <th>Quantità</th>
            {isSuperAdmin && <th className="hidden lg:table-cell">P. Acquisto</th>}
            <th className="hidden lg:table-cell">P. Vendita</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-center text-muted-foreground py-8">
                Nessun articolo trovato
              </td>
            </tr>
          ) : (
            rows.map((row) =>
              row.type === 'group' ? renderGroupRow(row) : renderSingleRow(row.articolo)
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
